import { useEffect, useRef, useState } from 'react';

// Top-right date pill that opens a small reference calendar popover on click.
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export function DatePill() {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);

  const today = new Date();
  const todayO = { y: today.getFullYear(), m: today.getMonth(), d: today.getDate() };
  const [view, setView] = useState({ y: todayO.y, m: todayO.m });
  const [selected, setSelected] = useState(todayO);

  const label = today.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const firstWeekday = new Date(view.y, view.m, 1).getDay();
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);

  const shift = (delta) => setView((v) => {
    const dt = new Date(v.y, v.m + delta, 1);
    return { y: dt.getFullYear(), m: dt.getMonth() };
  });
  const goToday = () => { setView({ y: todayO.y, m: todayO.m }); setSelected(todayO); };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`hidden sm:flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium ring-1 transition
          ${open ? 'bg-brand text-white ring-brand' : 'bg-white/70 dark:bg-white/10 text-slate-600 dark:text-slate-300 ring-white/60 dark:ring-white/15 hover:bg-white dark:hover:bg-white/15'}`}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <svg className={`h-4 w-4 ${open ? 'text-white' : 'text-brand dark:text-brand-light'}`} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
          <rect x="3" y="4.5" width="14" height="12" rx="2" />
          <path strokeLinecap="round" d="M3 8h14M7 3v3M13 3v3" />
        </svg>
        {label}
      </button>

      {open && (
        <div className="absolute right-0 z-40 mt-2 w-72 rounded-xl bg-white dark:bg-slate-800 p-3 shadow-xl ring-1 ring-slate-200 dark:ring-white/10">
          <div className="flex items-center justify-between px-1">
            <button type="button" onClick={() => shift(-1)} className="rounded-md p-1 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10" aria-label="Previous month">
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M12 5l-5 5 5 5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
            <div className="text-sm font-semibold text-navy-800 dark:text-slate-100">
              {new Date(view.y, view.m, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
            </div>
            <button type="button" onClick={() => shift(1)} className="rounded-md p-1 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10" aria-label="Next month">
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M8 5l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </div>

          <div className="mt-2 grid grid-cols-7 gap-1 text-center text-xs text-slate-400 dark:text-slate-500">
            {WEEKDAYS.map((w) => <div key={w} className="py-1">{w}</div>)}
          </div>

          <div className="grid grid-cols-7 gap-1 text-center text-sm">
            {cells.map((d, i) => {
              if (d === null) return <div key={`e${i}`} />;
              const isToday = todayO.y === view.y && todayO.m === view.m && todayO.d === d;
              const isSel = selected.y === view.y && selected.m === view.m && selected.d === d;
              return (
                <button
                  key={`d${d}`}
                  type="button"
                  onClick={() => setSelected({ y: view.y, m: view.m, d })}
                  className={`h-8 w-8 rounded-lg leading-8 transition ${
                    isSel ? 'bg-brand text-white font-semibold'
                      : 'text-slate-700 dark:text-slate-300 hover:bg-brand/10 dark:hover:bg-brand/20'
                  } ${!isSel && isToday ? 'ring-1 ring-brand/40' : ''}`}
                >
                  {d}
                </button>
              );
            })}
          </div>

          <div className="mt-3 flex justify-center">
            <button type="button" onClick={goToday} className="rounded-lg bg-brand/10 dark:bg-brand/20 px-4 py-1 text-xs font-medium text-brand-dark dark:text-brand-light hover:bg-brand/20 dark:hover:bg-brand/30">
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
